import { useTopOfPageContextStore } from "@faire/deprecated-retailer-visitor-shared/stores/ui/TopOfPageContextStore";
import { debounceIfPossible } from "@faire/web/common/globals/debounceIfPossible";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import { useCallback, useEffect, useMemo, useRef } from "react";

export const useResizeRefCallback = () => {
  const [_, setStore] = useTopOfPageContextStore([]);
  const contentRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | undefined>(undefined);

  const setHeaderHeight = useCallback(
    (height: number | undefined) => {
      if (height !== undefined) {
        setStore({ height });
      }
    },
    [setStore]
  );

  const handleResize = useCallback(() => {
    setHeaderHeight(contentRef.current?.clientHeight ?? 0);
  }, [setHeaderHeight]);

  const debouncedHandleResize = useMemo(
    () =>
      debounceIfPossible(() => {
        handleResize();
      }, 500),
    [handleResize]
  );

  useEffect(() => {
    const _window = getWindowOrThrow();
    _window.addEventListener("resize", debouncedHandleResize);
    return () => {
      _window.removeEventListener("resize", debouncedHandleResize);
    };
  }, [debouncedHandleResize]);

  useEffect(() => {
    return () => {
      if (contentRef.current) {
        resizeObserverRef.current?.unobserve(contentRef.current);
      }
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = undefined;
      contentRef.current = null;
    };
  }, []);

  const contentCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        const _window = getWindowOrThrow();
        const observer = _window.ResizeObserver
          ? new _window.ResizeObserver((entry) => {
              _window.requestAnimationFrame(() => {
                if (!entry) {
                  return;
                }
                setHeaderHeight(entry[0]?.contentRect.height ?? 0);
              });
            })
          : undefined;
        observer?.observe(node);
        contentRef.current = node;
        setHeaderHeight(contentRef.current.clientHeight);
        if (observer) {
          resizeObserverRef.current = observer;
        }
      }
    },
    [setHeaderHeight]
  );

  return { contentCallback, contentRef };
};
